'use client'

import { useQuery } from '@apollo/client/react'
import { FormProvider } from '@ariakit/react'
import { ComponentProps, useEffect } from 'react'
import styled from 'styled-components'

import {
  IconButton,
  QuickBuy as QuickBuyIcon,
  ResponsiveImage,
  Skeleton,
  Spacer,
  TypeStyleHeadlineExtraSmall,
  fromMd,
  untilMd,
} from '@syconium/little-miss-figgy'

import { gql } from '../../../__generated__/graphql/catalog/gql'
import {
  ProductCatalogCategoryKey,
  ProductCatalogGender,
} from '../../../__generated__/graphql/catalog/graphql'
import { Fit } from '../../../constants'
import { useUserPreferenceForCategory } from '../../_hooks/useUserPreferenceForCategory'
import { TrackingLocationProvider } from '../../_providers/TrackingLocationProvider.client'
import { useTrackProductImpression } from '../../_providers/TrackingProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import {
  VariantSelectionColorHeader,
  VariantSelectionColorRadioGroup,
} from '../forms/VariantSelectionColorField'
import { VariantSelectionFitRadioGroup } from '../forms/VariantSelectionFitField'
import {
  VariantSelectionForm,
  VariantSelectionFormFields,
  useVariantSelectionForm,
} from '../forms/VariantSelectionForm'
import { VariantSelectionSizeRadioGroup } from '../forms/VariantSelectionSizeField'
import { VariantSelectionFormPurchasingSubmit } from '../forms/VariantSelectionSubmit'
import { NextLink } from '../navigation/NextLink'
import {
  PricingRange,
  derivePricingForVariant,
  derivePricingForVariantGroup,
} from '../pricing/PricingRange.client'
import { ProductTileDetails, ProductTilePrice, ProductTileTitle } from '../tiles/ProductTile.client'

import { NextDialogProvider, NextDialogTrigger } from './NextDialog.client'
import { NextDrawerDialog } from './NextDrawerDialog.client'

import type { StaticTrackingSecondaryLocation } from '../../_hooks/useStaticTracking'

type QuickBuyDialogProps = QuickBuyDialogFormProps & {
  position: 'inline' | 'bottom-right'
} & ComponentProps<'button'>

export const QuickBuyDialog = styled(
  ({
    initialColor,
    collectionHandle,
    productGroupHandle,
    gender,
    categoryKey,
    secondaryTrackingLocation,
    ...props
  }: QuickBuyDialogProps) => {
    const translations = useTranslation()
    return (
      <NextDialogProvider>
        <NextDialogTrigger
          render={
            <IconButton
              type='button'
              variant='black-on-white'
              size='small'
              aria-label={translations.quickBuy.modal.headline}
              style={{ padding: '0px' }}
              {...props}
            >
              <QuickBuyIcon width='30px' height='30px' />
            </IconButton>
          }
        />
        <NextDrawerDialog
          analyticsTitle='Quickbuy'
          unmountOnHide={true}
          variant='bottom-drawer'
          closeText={translations.magnolia.general.close}
        >
          <QuickBuyContent>
            <TitleBar>
              <TypeStyleHeadlineExtraSmall textTransform='uppercase'>
                {translations.quickBuy.modal.headline}
              </TypeStyleHeadlineExtraSmall>
            </TitleBar>
            <TrackingLocationProvider isQuickbuy>
              <FormProvider>
                <QuickBuyDialogForm
                  productGroupHandle={productGroupHandle}
                  initialColor={initialColor}
                  collectionHandle={collectionHandle}
                  gender={gender}
                  categoryKey={categoryKey}
                  secondaryTrackingLocation={secondaryTrackingLocation}
                />
              </FormProvider>
            </TrackingLocationProvider>
          </QuickBuyContent>
        </NextDrawerDialog>
      </NextDialogProvider>
    )
  }
)`
  position: ${o => (o.position === 'inline' ? 'static' : 'absolute')};
  ${o =>
    o.position === 'inline'
      ? null
      : `
    z-index: ${o.theme.zIndex.foregroundContent};  
  `};
  ${untilMd} {
    bottom: ${o => o.theme.spacing(2)};
    right: ${o => o.theme.spacing(2)};
  }
  ${fromMd} {
    bottom: ${o => o.theme.spacing(3)};
    right: ${o => o.theme.spacing(3)};
  }
`

type QuickBuyDialogFormProps = {
  productGroupHandle: string
  collectionHandle: string | null | undefined
  initialColor: string
  gender: ProductCatalogGender
  categoryKey?: ProductCatalogCategoryKey
  secondaryTrackingLocation: StaticTrackingSecondaryLocation
}

const QuickBuyDialogForm: React.FC<QuickBuyDialogFormProps> = ({
  initialColor,
  gender,
  categoryKey,
  productGroupHandle,
  collectionHandle,
  secondaryTrackingLocation,
}) => {
  const { trackProductImpression } = useTrackProductImpression()
  const { preferredSizeId, preferredFitId } = useUserPreferenceForCategory({
    gender,
    categoryKey,
  })

  const { formStore, queryVariables } = useVariantSelectionForm({
    defaultValues: {
      color: initialColor,
      fit: preferredFitId || Fit.REGULAR,
      size: preferredSizeId,
      quantity: 1,
    },
  })

  const {
    data: currentData,
    previousData,
    loading,
    error,
  } = useQuery(
    gql(`
      query GetQuickBuyProductInfo(
          $collectionHandle: String, 
          $catalogProductHandle: String!, 
          $colorFieldValue: String!, 
          $fitFieldValue: String!, 
          $sizeFieldValue: String!
      ) {
        productCatalogStandardProduct(handle: $catalogProductHandle) {
          ...VariantSelectionFormProductFields
          variantGroup(color: $colorFieldValue) {
            ...VariantSelectionFormGroupFields
            gender
            urlPath
            images(first: 1) {
              url
              altText
            }
            defaultShopifyProduct {
              id
            }
            priceRange {
              currencyCode
              min {
                amount
              }
              max {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              min {
                amount
              }
              max {
                amount
              }
              minPercentageOff
              maxPercentageOff
            }
          }
        }
        productGroupVariantLabels(handle: $catalogProductHandle) {
          size
        }
      }
    `),
    {
      variables: {
        ...queryVariables,
        catalogProductHandle: productGroupHandle,
        collectionHandle,
      },
    }
  )

  const data = currentData ?? previousData

  useEffect(() => {
    if (data && !loading && !error) {
      trackProductImpression({
        featuredProduct: true,
        handle: data.productCatalogStandardProduct.handle,
        id: data.productCatalogStandardProduct.variantGroup.id,
        shopifyId:
          data.productCatalogStandardProduct.variantGroup.variant?.shopifyProductId ??
          data.productCatalogStandardProduct.variantGroup.defaultShopifyProduct.id,
        soldOut: data.productCatalogStandardProduct.variantGroup.availability === 'OUT_OF_STOCK',
        color: data.productCatalogStandardProduct.variantGroup.color?.id,
      })
    }
  }, [data, error, loading, trackProductImpression])

  if (!data) {
    if (loading) {
      return <QuickbuyFormSkeleton />
    } else {
      // TODO: Create and render a QuickBuyFormNotFound component
      return null
    }
  }

  if (error) {
    // TODO: Create and render a QuickBuyFormNotFound component
    return null
  }

  const product = data.productCatalogStandardProduct
  const variantGroup = data.productCatalogStandardProduct.variantGroup
  const selectedVariant = data.productCatalogStandardProduct.variantGroup.variant

  // Rides the query above, so it is already resolved by the time the skeleton clears.
  const sizeLabelOverride = data.productGroupVariantLabels?.size

  const pdpUrl = selectedVariant?.urlPath ?? variantGroup.urlPath
  const selectedImage = variantGroup.images?.[0]?.url
  const pricingRange = selectedVariant
    ? derivePricingForVariant(selectedVariant)
    : derivePricingForVariantGroup(variantGroup)

  return (
    <>
      <ProductPreview>
        {selectedImage && (
          <NextLink href={pdpUrl}>
            <ProductImage
              srcs={{
                sm: selectedImage,
                md: selectedImage,
              }}
              src={selectedImage}
              widths={{ unit: 'px', sm: 100, md: 100 }}
              alt={variantGroup.images?.[0]?.altText || variantGroup.title}
              loading='default'
              aspectRatios={{ sm: 1, md: 1 }}
            />
          </NextLink>
        )}
        <ProductTileDetails untilMdLeftPadding={false} noMargin>
          <NextLink href={pdpUrl}>
            <ProductTileTitle>{variantGroup.title}</ProductTileTitle>
          </NextLink>
          {pricingRange ? (
            <ProductTilePrice>
              <PricingRange {...pricingRange} />
            </ProductTilePrice>
          ) : null}
        </ProductTileDetails>
      </ProductPreview>
      <VariantSelectionForm
        store={formStore}
        product={product}
        selectedVariant={selectedVariant}
        variantGroup={variantGroup}
        secondaryTrackingLocation={secondaryTrackingLocation}
        loading={loading}
        sizeLabelOverride={sizeLabelOverride}
      >
        <VariantSelectionFormFields>
          <div>
            <VariantSelectionColorHeader />
            <VariantSelectionColorRadioGroup priceBucketing='enabled' />
          </div>
          <VariantSelectionSizeRadioGroup />
          <VariantSelectionFitRadioGroup />
        </VariantSelectionFormFields>

        <QuickBuyDialogFooter>
          <VariantSelectionFormPurchasingSubmit />
        </QuickBuyDialogFooter>
      </VariantSelectionForm>
    </>
  )
}

const QuickbuyFormSkeleton: React.FC = () => (
  <div>
    <ProductPreview>
      <Skeleton height={'80px'} width={'80px'}></Skeleton>
      <ProductTileDetails untilMdLeftPadding={false} noMargin>
        <Skeleton height={'20px'} width={'100px'}></Skeleton>
        <Skeleton height={'20px'} width={'100px'}></Skeleton>
      </ProductTileDetails>
    </ProductPreview>
    <Spacer height={24} width={1}></Spacer>
    <Skeleton height={'30px'} width={'150px'}></Skeleton>
    <Spacer height={8} width={1}></Spacer>
    <Skeleton height={'30px'} width={'300px'}></Skeleton>
    <Spacer height={24} width={1}></Spacer>
    <Skeleton height={'30px'} width={'150px'}></Skeleton>
    <Spacer height={8} width={1}></Spacer>
    <Skeleton height={'30px'} width={'300px'}></Skeleton>
    <Spacer height={24} width={1}></Spacer>
    <Skeleton height={'30px'} width={'150px'}></Skeleton>
    <Spacer height={8} width={1}></Spacer>
    <Skeleton height={'30px'} width={'300px'}></Skeleton>
    <Spacer height={24} width={1}></Spacer>
    <Skeleton height={'50px'} width={'100%'}></Skeleton>
    <Spacer height={24} width={1}></Spacer>
  </div>
)

const TitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${o => o.theme.spacing(4)};
`

const ProductPreview = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${o => o.theme.spacing(4)};
  gap: ${o => o.theme.spacing(2)};
`

const QuickBuyContent = styled.div`
  text-align: left;
  max-width: 812px;
  ${fromMd} {
    min-width: 812px;
  }
  margin-left: auto;
  margin-right: auto;
`

const ProductImage = styled(ResponsiveImage)`
  width: 80px;
`

const QuickBuyDialogFooter = styled.div`
  padding-top: ${o => o.theme.spacing(6)};
  width: 100%;
  display: flex;
  align-items: stretch;
  flex-direction: column;
`
